<script setup>
import { calculateScrollbarWidth, getViewport } from "@primeuix/utils/dom";
import { nextTick, onBeforeMount, ref, watch } from "vue";
import { useRoute } from "vue-router";

import FlowIcon from "@/assets/icons/navigation/FlowIcon.vue";
import AssistantIcon from "@/assets/icons/navigation/AssistantIcon.vue";
import WebhookIcon from "@/assets/icons/navigation/WebhookIcon.vue";
import { useLayout } from "@/layout/composables/layout";
import TalkToFounderIcon from "@/assets/icons/navigation/TalkToFounderIcon.vue";

const route = useRoute();

const {
  layoutConfig,
  layoutState,
  setActiveMenuItem,
  setMenuStates,
  setStaticMenuMobile,
  onMenuToggle,
  isHorizontal,
  isSlim,
  isSlimPlus,
  isDesktop,
  iconFillColor,
} = useLayout();

const iconColorStyle = (item) => {
  return isCustomIcon(item) ? undefined : { color: iconFillColor.value };
};
const props = defineProps({
  item: {
    type: Object,
    default: () => ({}),
  },
  index: {
    type: Number,
    default: 0,
  },
  root: {
    type: Boolean,
    default: true,
  },
  parentItemKey: {
    type: String,
    default: null,
  },
});

const customIcons = {
  FlowIcon,
  AssistantIcon,
  WebhookIcon,
  TalkToFounderIcon,
};

const iconComponent = (item) => {
  if (customIcons[item.icon]) {
    return customIcons[item.icon];
  } else {
    return "i";
  }
};

const isCustomIcon = (item) => {
  return customIcons[item.icon] !== undefined;
};

const isActiveMenu = ref(false);
const itemKey = ref(null);
const subMenuRef = ref(null);
const menuItemRef = ref(null);

onBeforeMount(() => {
  itemKey.value = props.parentItemKey
    ? props.parentItemKey + "-" + props.index
    : String(props.index);

  const activeItem = layoutState.activeMenuItem;

  isActiveMenu.value =
    activeItem === itemKey.value || activeItem
      ? activeItem.startsWith(itemKey.value + "-")
      : false;
  handleRouteChange(route.path);
});

watch(
  () => isActiveMenu.value,
  () => {
    const rootIndex = props.root
      ? props.index
      : parseInt(`${props.parentItemKey}`[0], 10);
    const overlay = document.querySelectorAll(".layout-root-submenulist")[
      rootIndex
    ];
    const target = document.querySelectorAll(".layout-root-menuitem")[
      rootIndex
    ];

    if ((isSlim.value || isSlimPlus.value || isHorizontal.value) && isDesktop) {
      nextTick(() => {
        calculatePosition(overlay, target);
      });
    }
  },
);

watch(
  () => layoutState.activeMenuItem,
  (newVal) => {
    isActiveMenu.value =
      newVal === itemKey.value || newVal.startsWith(itemKey.value + "-");
  },
);

watch(
  () => layoutConfig.menuMode,
  () => {
    isActiveMenu.value = false;
  },
);

watch(
  () => layoutState.overlaySubmenuActive,
  (newValue) => {
    if (!newValue) {
      isActiveMenu.value = false;
    }
  },
);

watch(() => route.path, handleRouteChange);

watch(
  () => route.path,
  (newPath) => {
    if (
      !(isSlim.value || isSlimPlus.value || isHorizontal.value) &&
      props.item.to &&
      props.item.to === newPath
    ) {
      setActiveMenuItem(itemKey);
    } else if (isSlim.value || isSlimPlus.value || isHorizontal.value) {
      isActiveMenu.value = false;
    }
  },
);

async function itemClick(event, item) {
  if (item.disabled) {
    event.preventDefault();
    return;
  }

  if (
    (item.to || item.url) &&
    (layoutState.staticMenuMobileActive || layoutState.overlayMenuActive)
  ) {
    onMenuToggle();
  }

  if (item.command) {
    item.command({ originalEvent: event, item: item });
  }

  if (item.items) {
    if (
      props.root &&
      isActiveMenu.value &&
      (isSlim.value || isSlimPlus.value || isHorizontal.value)
    ) {
      setMenuStates(false);

      return;
    }

    setActiveMenuItem(isActiveMenu.value ? props.parentItemKey : itemKey);

    if (
      props.root &&
      !isActiveMenu.value &&
      (isSlim.value || isSlimPlus.value || isHorizontal.value)
    ) {
      setMenuStates(true);
      isActiveMenu.value = true;

      removeAllTooltips();
    }
  } else {
    if (!isDesktop) {
      setStaticMenuMobile();
    }

    if (isSlim.value || isSlimPlus.value || isHorizontal.value) {
      setMenuStates(false);

      return;
    }

    setActiveMenuItem(itemKey);
  }
}

function handleRouteChange(newPath) {
  if (
    !(isSlim.value || isSlimPlus.value || isHorizontal.value) &&
    props.item.to &&
    props.item.to === newPath
  ) {
    setActiveMenuItem(itemKey);
  } else if (isSlim.value || isSlimPlus.value || isHorizontal.value) {
    isActiveMenu.value = false;
  }
}

function onMouseEnter() {
  if (
    props.root &&
    (isSlim.value || isSlimPlus.value || isHorizontal.value) &&
    isDesktop
  ) {
    if (!isActiveMenu.value && layoutState.menuHoverActive) {
      setActiveMenuItem(itemKey);
    }
  }
}

function removeAllTooltips() {
  const tooltips = document.querySelectorAll(".p-tooltip");
  tooltips.forEach((tooltip) => {
    tooltip.remove();
  });
}

function calculatePosition(overlay, target) {
  if (overlay) {
    const { left, top } = target.getBoundingClientRect();
    const { width: vWidth, height: vHeight } = getViewport();
    const [oWidth, oHeight] = [overlay.offsetWidth, overlay.offsetHeight];
    const scrollbarWidth = calculateScrollbarWidth();

    // reset
    overlay.style.top = overlay.style.left = "";

    if (isHorizontal.value) {
      const width = left + oWidth + scrollbarWidth;
      overlay.style.left =
        vWidth < width ? `${left - (width - vWidth)}px` : `${left}px`;
    } else if (isSlim.value || isSlimPlus.value) {
      const height = top + oHeight;
      overlay.style.top =
        vHeight < height ? `${top - (height - vHeight)}px` : `${top}px`;
    }
  }
}

function checkActiveRoute(item) {
  return route.path === item.to;
}
</script>

<template>
  <li
    ref="menuItemRef"
    :class="{ 'layout-root-menuitem': root, 'active-menuitem': isActiveMenu }"
  >
    <div
      v-if="root && item.visible !== false"
      class="layout-menuitem-root-text"
    >
      {{ item.label }}
    </div>
    <!-- External link -->
    <a
      v-if="(!item.to || item.items) && item.visible !== false"
      v-tooltip.hover="isSlim && root && !isActiveMenu ? item.label : null"
      :href="item.url"
      :class="item.class"
      :target="item.target || '_blank'"
      tabindex="0"
      @click="itemClick($event, item, index)"
      @mouseenter="onMouseEnter"
    >
      <component
        :is="iconComponent(item)"
        :fill-color="isCustomIcon(item) ? iconFillColor : undefined"
        :class="item.icon"
        class="layout-menuitem-icon"
        :style="iconColorStyle(item)"
      />
      <span class="layout-menuitem-text">{{ item.label }}</span>
      <i
        v-if="item.items"
        class="pi pi-fw pi-angle-down layout-submenu-toggler"
      ></i>
    </a>
    <!-- Internal link -->
    <router-link
      v-if="item.to && !item.items && item.visible !== false"
      v-tooltip.hover="
        (isSlim || isSlimPlus) && root && !isActiveMenu ? item.label : null
      "
      :class="[item.class, { 'active-route': checkActiveRoute(item) }]"
      tabindex="0"
      :to="item.to"
      @click="itemClick($event, item, index)"
      @mouseenter="onMouseEnter"
    >
      <component
        :is="iconComponent(item)"
        :fill-color="isCustomIcon(item) ? iconFillColor : undefined"
        :class="item.icon"
        class="layout-menuitem-icon"
        :style="iconColorStyle(item)"
      />
      <span class="layout-menuitem-text">{{ item.label }}</span>
      <i
        v-if="item.items"
        class="pi pi-fw pi-angle-down layout-submenu-toggler"
      ></i>
    </router-link>

    <ul
      v-if="item.items && item.visible !== false"
      ref="subMenuRef"
      :class="{ 'layout-root-submenulist': root }"
    >
      <AppMenuItem
        v-for="(child, i) in item.items"
        :key="child"
        :index="i"
        :item="child"
        :parent-item-key="itemKey"
        :root="false"
      ></AppMenuItem>
    </ul>
  </li>
</template>
